import { useEffect, useState } from "react";
import type { Authorization, Order } from "../../api/types";
import { useTill } from "../../app/till-context";
import { businessDayOf } from "./days";
import { OrdersScreen } from "./OrdersScreen";
import type { OrdersState } from "./useOrders";

const NOTHING_NEW: ReadonlySet<string> = new Set();
const nothing = () => undefined;

interface Props {
  /** Today's orders, live. */
  live: OrdersState;
  authorization: Authorization;
  onEdit: (order: Order, authorization: Authorization | null) => void;
  ownerBeheer?: Authorization | undefined;
}

/**
 * Beheer › Bestellingen with a small calendar on top (Furkan, 09/10/2026: "via een kleine agenda knopje de
 * bestellingen per dag kunnen zien, bv. bestellingen van 4 oktober"): today live as before, an earlier day
 * fetched once and shown to look at and print, never to change.
 */
export function OrdersByDay({ live, authorization, onEdit, ownerBeheer }: Props) {
  const { api, report } = useTill();
  const today = businessDayOf(new Date());
  const [day, setDay] = useState(today);
  const [past, setPast] = useState<{ day: string; orders: Order[] } | null>(null);

  useEffect(() => {
    if (day === today) return;
    const controller = new AbortController();
    api
      .request<Order[]>("GET", `/orders?businessDay=${day}`, {
        staffToken: authorization.token,
        retry: true,
        signal: controller.signal,
      })
      .then((orders) => {
        setPast({ day, orders });
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) report(error);
      });
    return () => {
      controller.abort();
    };
  }, [api, authorization.token, day, today, report]);

  /** What the owner changes on an earlier day (→ Afhaal): that day's copy follows at once, nothing live comes for it. */
  const upsertPast = (order: Order) => {
    setPast((current) =>
      current === null
        ? current
        : { ...current, orders: current.orders.map((each) => (each.id === order.id ? order : each)) },
    );
  };

  const shown = past?.day === day ? past.orders : null;
  const state: OrdersState =
    day === today
      ? live
      : {
          orders: shown ?? [],
          loaded: shown !== null,
          recent: NOTHING_NEW,
          reload: nothing,
          apply: nothing,
          upsert: upsertPast,
        };
  return (
    <OrdersScreen
      state={state}
      onEdit={onEdit}
      admin
      ownerBeheer={ownerBeheer}
      day={{ value: day, today, onChange: setDay }}
    />
  );
}
